import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { UserPlusIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { agentService } from '../../services/agentService';
import { leadService } from '../../services/leadService';
import { TaggedAgent } from '../../types/lead';
import Button from '../UI/Button';

interface AgentOption {
  id: string;
  name: string;
  email?: string;
}

interface AgentTagPickerProps {
  leadId: string;
  taggedAgents?: TaggedAgent[];
  onUpdated?: (taggedAgents: TaggedAgent[]) => void;
  className?: string;
  compact?: boolean;
}

const MENU_WIDTH = 280;
const MENU_MAX_HEIGHT = 320;

const AgentTagPicker: React.FC<AgentTagPickerProps> = ({
  leadId,
  taggedAgents = [],
  onUpdated,
  className = '',
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<AgentOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, maxHeight: MENU_MAX_HEIGHT });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setLoading(true);
    agentService
      .getTagOptions()
      .then(setOptions)
      .catch(() => toast.error('Failed to load agents'))
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => searchRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };

    const onScroll = (e: Event) => {
      const target = e.target as Node | null;
      if (menuRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      document.addEventListener('scroll', onScroll, true);
      document.addEventListener('keydown', onKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const taggedIds = new Set(taggedAgents.map((a) => a.id));

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (a) => a.name.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q)
    );
  }, [options, search]);

  const saveTags = async (nextIds: string[]) => {
    setSaving(true);
    try {
      const updated = await leadService.updateLead(leadId, { taggedAgentIds: nextIds });
      const nextTagged = updated.taggedAgents || updated.tagged_agents || [];
      onUpdated?.(nextTagged);
    } catch {
      toast.error('Failed to update tagged agents');
    } finally {
      setSaving(false);
    }
  };

  const toggleAgent = async (agentId: string) => {
    const nextIds = taggedIds.has(agentId)
      ? taggedAgents.filter((a) => a.id !== agentId).map((a) => a.id)
      : [...taggedAgents.map((a) => a.id), agentId];
    await saveTags(nextIds);
  };

  const removeTag = async (agentId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await saveTags(taggedAgents.filter((a) => a.id !== agentId).map((a) => a.id));
  };

  const computeMenuPosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const left = Math.min(
      Math.max(12, rect.left),
      window.innerWidth - MENU_WIDTH - 12
    );

    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const openUpward = spaceBelow < 220 && spaceAbove > spaceBelow;

    const maxHeight = Math.min(
      MENU_MAX_HEIGHT,
      openUpward ? spaceAbove - 8 : spaceBelow - 8
    );

    const top = openUpward
      ? Math.max(12, rect.top - maxHeight - 6)
      : rect.bottom + 6;

    setMenuPos({ top, left, maxHeight: Math.max(160, maxHeight) });
  };

  const openMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    computeMenuPosition();
    setOpen(true);
  };

  return (
    <div className={className} onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-col gap-1.5">
        {!compact && (
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Tagged agents
          </span>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          {taggedAgents.map((agent) => (
            <span
              key={agent.id}
              className="inline-flex max-w-[11rem] items-center gap-1 rounded-md border border-primary/25 bg-primary/10 px-2 py-1 text-xs font-medium text-foreground"
            >
              <span className="truncate">{agent.name}</span>
              <button
                type="button"
                onClick={(e) => removeTag(agent.id, e)}
                disabled={saving}
                className="shrink-0 rounded p-0.5 hover:bg-primary/20 hover:text-destructive disabled:opacity-50"
                aria-label={`Remove ${agent.name}`}
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            ref={triggerRef}
            type="button"
            onClick={openMenu}
            disabled={saving}
            aria-expanded={open}
            aria-haspopup="listbox"
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
              open
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-dashed border-border bg-background text-muted-foreground hover:border-primary hover:text-primary'
            }`}
          >
            <UserPlusIcon className="h-3.5 w-3.5" />
            {taggedAgents.length > 0 ? `Add tag (${taggedAgents.length})` : 'Tag agents'}
          </button>
        </div>
      </div>

      {open &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[9998]"
              aria-hidden
              onClick={() => setOpen(false)}
            />
            <div
              ref={menuRef}
              role="dialog"
              aria-label="Tag agents"
              style={{
                top: menuPos.top,
                left: menuPos.left,
                width: MENU_WIDTH,
                maxHeight: menuPos.maxHeight,
              }}
              className="fixed z-[9999] flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="shrink-0 border-b border-border px-3 py-2.5">
                <p className="text-sm font-semibold text-foreground">Tag agents</p>
                <p className="text-xs text-muted-foreground">Select one or more agents</p>
              </div>

              <div className="shrink-0 border-b border-border px-3 py-2">
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    ref={searchRef}
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search agents..."
                    className="h-8 w-full rounded-lg border border-border bg-background pl-8 pr-3 text-xs text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </div>
              </div>

              <div
                ref={listRef}
                className="scroll-area min-h-0 flex-1 overflow-y-auto overscroll-contain"
                style={{ WebkitOverflowScrolling: 'touch' }}
                onWheel={(e) => e.stopPropagation()}
              >
                {loading ? (
                  <p className="px-3 py-4 text-center text-xs text-muted-foreground">Loading agents...</p>
                ) : filteredOptions.length === 0 ? (
                  <p className="px-3 py-4 text-center text-xs text-muted-foreground">
                    {search ? 'No agents match your search' : 'No agents available'}
                  </p>
                ) : (
                  <ul className="py-1" role="listbox" aria-multiselectable="true">
                    {filteredOptions.map((agent) => {
                      const selected = taggedIds.has(agent.id);
                      return (
                        <li key={agent.id}>
                          <label
                            className={`flex cursor-pointer items-center gap-2.5 px-3 py-2.5 transition-colors hover:bg-muted/50 ${
                              selected ? 'bg-primary/5' : ''
                            } ${saving ? 'pointer-events-none opacity-60' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              disabled={saving}
                              onChange={() => void toggleAgent(agent.id)}
                              className="h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-primary/30"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm text-foreground">{agent.name}</span>
                              {agent.email && (
                                <span className="block truncate text-[11px] text-muted-foreground">{agent.email}</span>
                              )}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="flex shrink-0 items-center justify-between border-t border-border bg-muted/20 px-3 py-2">
                <span className="text-xs text-muted-foreground">
                  {taggedAgents.length} selected
                </span>
                <Button size="sm" variant="outlined" onClick={() => setOpen(false)}>
                  Done
                </Button>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default AgentTagPicker;
